/**
 * Client Info tab — mirrors the KC2 reference layout, adapted to kc3 data:
 *   1) a full-width client profile hero (identity + totals + avg CSAT)
 *   2) a row of status stat-cards (Open / Reopened / On hold / Closed)
 *   3) a table of the client's past conversations
 *
 * Data is the client's conversations, fetched by contactId. All corners ≤3px.
 */
import { useMemo, useState, type JSX } from 'react'
import { Check, CheckCircle2, FolderOpen, GitMerge, Mail, PauseCircle, RotateCcw, Star } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { ConversationDetailResponse } from '../../../modules/conversations/types.js'
import { getChannelConfig } from '../../../modules/conversations/channelConfig.js'
import { formatDateTime } from '../../../utils/timezone.js'
import { useRecentConversations } from '../../../hooks/useRecentConversations.js'
import { Avatar, R, StatusPill } from './primitives.js'
import type { MergeCandidate } from './MergeDrawer.js'

type Conversation = ConversationDetailResponse['conversation']

export interface ClientInfoTabProps {
  conversation: Conversation
  onNavigateToConversation?: ((conversationId: string) => void) | undefined
  onMergeTickets?: ((candidates: MergeCandidate[]) => void) | undefined
}

const STAT_CARDS: { key: string; label: string; icon: LucideIcon; color: string; bg: string }[] = [
  { key: 'open', label: 'Open', icon: FolderOpen, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { key: 'reopen', label: 'Reopened', icon: RotateCcw, color: 'text-blue-600', bg: 'bg-blue-50' },
  { key: 'on_hold', label: 'On hold', icon: PauseCircle, color: 'text-amber-600', bg: 'bg-amber-50' },
  { key: 'closed', label: 'Closed', icon: CheckCircle2, color: 'text-slate-500', bg: 'bg-slate-100' },
]

function Stars({ value }: { value: number }): JSX.Element {
  const rounded = Math.round(value)
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-4 w-4 ${n <= rounded ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
        />
      ))}
    </span>
  )
}

export default function ClientInfoTab({
  conversation,
  onNavigateToConversation,
  onMergeTickets,
}: ClientInfoTabProps): JSX.Element {
  const hasClientId = Boolean(conversation.clientId)
  const { conversations, loading } = useRecentConversations(
    hasClientId ? conversation.clientId : conversation.contactId,
    50,
    hasClientId ? 'client' : 'contact',
  )
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const counts = useMemo(() => {
    const c: Record<string, number> = { open: 0, reopen: 0, on_hold: 0, closed: 0 }
    for (const conv of conversations) if (conv.status in c) c[conv.status]! += 1
    return c
  }, [conversations])

  const avgCsat = useMemo(() => {
    const scores = conversations.map((c) => c.csatScore).filter((s): s is number => s !== null)
    if (scores.length === 0) return 0
    return scores.reduce((a, b) => a + b, 0) / scores.length
  }, [conversations])

  const fullName = conversation.displayName || conversation.primaryHandle || 'Unknown'
  const channel = getChannelConfig(conversation.channelOrigin)

  return (
    <div className="w-full space-y-4">
      {/* 1) Full-width profile hero */}
      <div className={`overflow-hidden ${R} border border-slate-200 bg-white shadow-sm`}>
        <div className="flex flex-col gap-5 bg-gradient-to-r from-[#F5F7FF] via-white to-white p-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <Avatar name={fullName} size={60} />
            <div className="min-w-0">
              <div className="truncate text-xl font-bold text-[#0f172a]">{fullName}</div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 text-slate-400" />
                  {conversation.primaryHandle ?? '—'}
                </span>
                <span
                  className={`inline-flex items-center gap-1 ${R} ${channel.bg} px-2 py-0.5 text-xs font-medium ${channel.color}`}
                >
                  <channel.icon className="h-3 w-3" />
                  {channel.label}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-8 md:pr-2">
            <div className="text-center">
              <div className="text-2xl font-bold text-[#3366FF]">{conversations.length}</div>
              <div className="text-xs text-slate-400">Conversations</div>
            </div>
            <span className="h-10 w-px bg-slate-200" />
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <Stars value={avgCsat} />
                <span className="text-sm font-semibold text-slate-700">{avgCsat ? avgCsat.toFixed(1) : '—'}</span>
              </div>
              <div className="mt-0.5 text-xs text-slate-400">Average CSAT</div>
            </div>
          </div>
        </div>
      </div>

      {/* 2) Status stat cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {STAT_CARDS.map((s) => (
          <div key={s.key} className={`flex items-center gap-3 ${R} border border-slate-200 bg-white p-4`}>
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center ${R} ${s.bg} ${s.color}`}>
              <s.icon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800">{counts[s.key] ?? 0}</div>
              <div className="text-xs text-slate-400">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* 3) Interactions table */}
      <div className={`overflow-hidden ${R} border border-slate-200 bg-white`}>
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
            Client conversations
          </span>
          {selectedIds.size > 0 && onMergeTickets && (
            <button
              type="button"
              onClick={() => {
                const picked: MergeCandidate[] = conversations
                  .filter((c) => selectedIds.has(c.id))
                  .map((c) => ({
                    id: c.id,
                    referenceId: c.referenceId,
                    subject: c.subject ?? null,
                    status: c.status ?? null,
                    requester: c.displayName || c.primaryHandle || null,
                    // Relationship state comes along so the merge dialog can
                    // say which conversation is ineligible, and why, before
                    // the agent submits.
                    parentReferenceId: c.parentReferenceId ?? null,
                    childCount: c.childCount ?? 0,
                    mergedIntoReferenceId: c.mergedIntoReferenceId ?? null,
                    secondaryCount: c.secondaryCount ?? 0,
                    linkedRelationCount: c.linkedRelationCount ?? 0,
                    approvalStatus: c.approvalStatus ?? null,
                    approvalStateId: c.approvalStateId ?? null,
                  }))
                onMergeTickets(picked)
                setSelectedIds(new Set())
              }}
              className="inline-flex items-center gap-1.5 rounded-[3px] bg-[#3366FF] px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-[#2952CC] active:scale-[0.98]"
            >
              <GitMerge className="h-3.5 w-3.5" />
              Merge {selectedIds.size} conversation{selectedIds.size === 1 ? '' : 's'}
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/70 text-left text-xs font-semibold text-slate-500">
                {onMergeTickets && <th className="w-10 px-4 py-2.5"></th>}
                <th className="px-4 py-2.5">Reference</th>
                <th className="px-4 py-2.5">Subject</th>
                <th className="px-4 py-2.5">Channel</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={onMergeTickets ? 6 : 5} className="px-4 py-8 text-center text-sm text-slate-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && conversations.length === 0 && (
                <tr>
                  <td colSpan={onMergeTickets ? 6 : 5} className="px-4 py-8 text-center text-sm text-slate-400">
                    No other conversations for this client.
                  </td>
                </tr>
              )}
              {!loading &&
                conversations.map((c) => {
                  const ch = getChannelConfig(c.channelOrigin)
                  const isCurrent = c.id === conversation.id
                  const isSelected = selectedIds.has(c.id)
                  return (
                    <tr
                      key={c.id}
                      className={`border-b border-slate-50 last:border-0 ${isCurrent ? 'bg-[#F5F7FF]' : isSelected ? 'bg-blue-50/50' : 'hover:bg-slate-50'} ${onNavigateToConversation ? 'cursor-pointer' : ''}`}
                      onClick={() => {
                        if (isCurrent) return
                        onNavigateToConversation?.(c.id)
                      }}
                    >
                      {onMergeTickets && (
                        <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <label
                            className={`relative flex h-[18px] w-[18px] cursor-pointer items-center justify-center rounded-[3px] border-2 transition-all ${
                              isSelected
                                ? 'border-[#3366FF] bg-[#3366FF]'
                                : 'border-slate-300 bg-white hover:border-[#3366FF]/50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                setSelectedIds((prev) => {
                                  const next = new Set(prev)
                                  if (e.target.checked) next.add(c.id)
                                  else next.delete(c.id)
                                  return next
                                })
                              }}
                              className="absolute h-full w-full cursor-pointer opacity-0"
                              aria-label={`Select conversation ${c.referenceId} for merge`}
                            />
                            {isSelected && (
                              <Check className="pointer-events-none h-3 w-3 text-white" strokeWidth={3} />
                            )}
                          </label>
                        </td>
                      )}
                      <td className="whitespace-nowrap px-4 py-2.5 font-medium text-[#3366FF]">
                        #{c.referenceId}
                        {isCurrent && <span className="ml-1 text-[10px] text-slate-400">(current)</span>}
                      </td>
                      <td className="max-w-[320px] truncate px-4 py-2.5 text-slate-700">{c.subject || c.lastMessagePreview || '—'}</td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <span className={`inline-flex items-center gap-1 text-xs ${ch.color}`}>
                          <ch.icon className="h-3 w-3" />
                          {ch.label}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <StatusPill status={c.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-xs text-slate-500">
                        {formatDateTime(c.lastMessageAt) || '—'}
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
